import { useState, useEffect } from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Badge,
  Banner
} from '@shopify/polaris';

/**
 * ReferralBanner - Growth/viral mechanic
 * Each merchant gets a unique referral link
 * Referrer gets 30 days free, referred gets 30 days free
 */
export default function ReferralBanner({ shop, shopData, showToast }) {
  const [referralCode, setReferralCode] = useState('');
  const [referralCount, setReferralCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!shop) return;

    const loadReferral = async () => {
      try {
        const response = await fetch(`/api/referral?shop=${shop}`);
        if (response.ok) {
          const data = await response.json();
          setReferralCode(data.code || '');
          setReferralCount(data.referral_count || 0);
        }
      } catch (error) {
        // Generate client-side code as fallback
        const code = shop.replace('.myshopify.com', '').substring(0, 8).toUpperCase();
        setReferralCode(code);
      } finally {
        setLoading(false);
      }
    };

    loadReferral();
  }, [shop]);

  const referralLink = `https://apps.shopify.com/profit-pulse-ai?ref=${referralCode}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      showToast('Referral link copied!');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      showToast('Failed to copy', true);
    }
  };

  if (loading || !shop) return null;

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between">
          <Text variant="headingMd" as="h2">Refer & Earn</Text>
          {referralCount > 0 && (
            <Badge tone="success">{referralCount} referral(s)</Badge>
          )}
        </InlineStack>

        <Text variant="bodySm" as="p" tone="subdued">
          Share Profit Pulse AI with other merchants. You both get 30 days free when they install.
        </Text>

        <InlineStack gap="200" blockAlign="end">
          <div style={{ flex: 1 }}>
            <TextField
              label="Your referral link"
              labelHidden
              value={referralLink}
              readOnly
              autoComplete="off"
              connectedRight={
                <Button onClick={handleCopy} variant={copied ? 'primary' : undefined}>
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              }
            />
          </div>
        </InlineStack>

        {referralCount > 0 && (
          <Banner tone="success">
            <p>You've earned {referralCount * 30} free days from referrals! Keep sharing.</p>
          </Banner>
        )}
      </BlockStack>
    </Card>
  );
}
